import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Crown, Upload, Trash2, X, Plus, Users, Search } from 'lucide-react';
import api from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl my-4">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#561D1A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export default function PlatinumTab() {
  const { currency: activeCurrency, formatShortDate } = useCurrency();
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('members'); // members | transactions | import | discounts
  const [importModal, setImportModal] = useState(false);
  const [importForm, setImportForm] = useState([{ name: '', email: '', phone: '', gym_tier: 'premium', gym_member_id: '' }]);
  const [importing, setImporting] = useState(false);
  const csvRef = useRef(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    const [mRes, tRes, sRes] = await Promise.all([
      api.get('/api/membership/platinum/members').catch(() => ({ data: { members: [] } })),
      api.get('/api/membership/platinum/transactions').catch(() => ({ data: { transactions: [] } })),
      api.get('/api/admin/stores').catch(() => ({ data: [] })),
    ]);
    setMembers(mRes.data.members || []);
    setTransactions(tRes.data.transactions || []);
    setStores(sRes.data.filter ? sRes.data.filter(s => s.is_active) : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleImport = async () => {
    const valid = importForm.filter(m => m.name && m.email);
    if (!valid.length) { toast.error('Add at least one member'); return; }
    setImporting(true);
    try {
      const res = await api.post('/api/membership/platinum/import', { members: valid });
      toast.success(`${res.data.created} member(s) imported`);
      if (res.data.skipped > 0) toast.info(`${res.data.skipped} skipped`);
      if (res.data.errors > 0) toast.error(`${res.data.errors} error(s)`);
      setImportModal(false);
      setImportForm([{ name: '', email: '', phone: '', gym_tier: 'premium', gym_member_id: '' }]);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/membership/platinum/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.created} member(s) imported from CSV`);
      if (res.data.skipped > 0) toast.info(`${res.data.skipped} skipped`);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'CSV import failed');
    } finally { setImporting(false); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this Platinum member?')) return;
    try {
      await api.delete(`/api/membership/platinum/members/${id}`);
      toast.success('Member removed');
      load();
    } catch { toast.error('Failed to remove'); }
  };

  const handleStoreDiscount = async (storeId, percent) => {
    try {
      await api.put(`/api/admin/stores/${storeId}`, { platinum_discount_percent: parseFloat(percent) });
      toast.success('Discount updated');
      load();
    } catch { toast.error('Failed to update'); }
  };

  const addRow = () => setImportForm(f => [...f, { name: '', email: '', phone: '', gym_tier: 'premium', gym_member_id: '' }]);
  const updateRow = (i, field, val) => setImportForm(f => f.map((r, j) => j === i ? { ...r, [field]: val } : r));
  const removeRow = (i) => setImportForm(f => f.filter((_, j) => j !== i));

  const filtered = members.filter(m =>
    !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="platinum-tab">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 mb-5 border-b border-gray-100 pb-3">
        {[
          { key: 'members', label: 'Members', count: members.length },
          { key: 'discounts', label: 'Store Discounts' },
          { key: 'transactions', label: 'Transactions', count: transactions.length },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`text-sm px-4 py-2 rounded-xl transition-all ${tab === t.key ? 'bg-gradient-to-r from-[#D4AF37] to-[#F5E6A3] text-[#1a1a1a] font-bold shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`platinum-subtab-${t.key}`}>
            {t.label} {t.count !== undefined && <span className="ml-1 text-xs opacity-70">({t.count})</span>}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={() => setImportModal(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#c9a030] text-white px-4 py-2 rounded-xl text-sm font-medium shadow-sm hover:shadow-md transition-all"
          data-testid="import-platinum-btn">
          <Crown size={14} /> Import Members
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}</div>
      ) : (
        <>
          {/* ─── Members ─── */}
          {tab === 'members' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#D4AF37]" data-testid="search-platinum" />
                </div>
                <p className="text-xs text-gray-400">{filtered.length} member(s)</p>
              </div>
              {filtered.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Crown size={40} className="mx-auto mb-3 text-[#D4AF37]/30" />
                  <p>No Platinum members yet. Import gym Premium members to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map(m => (
                    <div key={m.id} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-[#D4AF37]/30 transition-colors" data-testid="platinum-member-row">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B6914] flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {m.name?.charAt(0)?.toUpperCase() || 'P'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800 text-sm">{m.name}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-[#D4AF37]/20 to-[#F5E6A3]/20 text-[#8B6914] font-semibold">PLATINUM</span>
                        </div>
                        <p className="text-xs text-gray-400 truncate">{m.email} {m.phone ? `· ${m.phone}` : ''}</p>
                        {m.card_number && <p className="text-[10px] text-gray-300 font-mono tracking-wider mt-0.5">{m.card_number.replace(/(.{4})/g, '$1 ').trim()}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Expires {m.expiry_date ? formatShortDate(m.expiry_date) : 'N/A'}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{m.status}</span>
                      </div>
                      <button onClick={() => handleDelete(m.id)} className="text-gray-300 hover:text-red-500 transition-colors" data-testid="delete-platinum-btn">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── Store Discounts ─── */}
          {tab === 'discounts' && (
            <div>
              <p className="text-sm text-gray-500 mb-4">Configure Platinum member discount percentage for each participating store.</p>
              {stores.length === 0 ? (
                <p className="text-center py-16 text-gray-400">No active stores</p>
              ) : (
                <div className="space-y-2">
                  {stores.map(s => (
                    <div key={s.id} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100" data-testid="store-discount-row">
                      <div className="flex-1">
                        <p className="font-medium text-gray-800 text-sm">{s.name}</p>
                        <p className="text-xs text-gray-400">{s.category || 'Uncategorized'} · {s.location_floor || ''} {s.location_unit || ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" min="0" max="50" step="0.5"
                          defaultValue={s.platinum_discount_percent || 0}
                          onBlur={e => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val !== (s.platinum_discount_percent || 0)) handleStoreDiscount(s.id, val);
                          }}
                          className="w-20 border border-gray-200 rounded-lg px-3 py-2 text-sm text-center outline-none focus:border-[#D4AF37]"
                          data-testid="store-discount-input"
                        />
                        <span className="text-sm text-gray-500 font-medium">%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── Transactions ─── */}
          {tab === 'transactions' && (
            <div>
              {transactions.length === 0 ? (
                <p className="text-center py-16 text-gray-400">No Platinum discount transactions yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 uppercase tracking-wider border-b border-gray-100">
                        <th className="text-left py-3 px-4">Member</th>
                        <th className="text-left py-3 px-4">Store</th>
                        <th className="text-right py-3 px-4">Bill</th>
                        <th className="text-right py-3 px-4">Discount</th>
                        <th className="text-right py-3 px-4">Customer Pays</th>
                        <th className="text-right py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(tx => (
                        <tr key={tx.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-800">{tx.member_name}</div>
                          </td>
                          <td className="py-3 px-4 text-gray-500">{tx.store_name || 'N/A'}</td>
                          <td className="py-3 px-4 text-right font-medium">{formatAmount(tx.bill_total, activeCurrency)}</td>
                          <td className="py-3 px-4 text-right text-[#D4AF37] font-bold">-{tx.discount_percent}% ({formatAmount(tx.discount_amount, activeCurrency)})</td>
                          <td className="py-3 px-4 text-right font-medium">{formatAmount(tx.customer_pays, activeCurrency)}</td>
                          <td className="py-3 px-4 text-right text-xs text-gray-400">{formatShortDate(tx.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ─── Import Modal ─── */}
      {importModal && (
        <Modal title="Import Gym Premium Members" onClose={() => setImportModal(false)}>
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-xl p-4">
              <p className="text-xs text-gray-600">
                Only <strong>Premium</strong> gym tier members qualify for Platinum status.
                Basic and Standard tiers will be skipped.
              </p>
            </div>

            {/* CSV Upload */}
            <div className="flex gap-2">
              <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={handleCsvImport} />
              <button onClick={() => csvRef.current?.click()} disabled={importing}
                className="flex items-center gap-2 text-sm border border-dashed border-[#D4AF37]/40 text-[#8B6914] px-4 py-2 rounded-xl hover:bg-[#D4AF37]/5 transition-colors disabled:opacity-50"
                data-testid="csv-import-platinum-btn">
                <Upload size={14} /> {importing ? 'Importing...' : 'Upload CSV'}
              </button>
              <span className="text-xs text-gray-400 self-center">Columns: name, email, phone, gym_tier, gym_member_id</span>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Or add manually</p>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {importForm.map((row, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input value={row.name} onChange={e => updateRow(i, 'name', e.target.value)}
                      placeholder="Name" className="col-span-3 text-xs border border-gray-200 rounded-lg px-2 py-2 outline-none focus:border-[#D4AF37]" />
                    <input value={row.email} onChange={e => updateRow(i, 'email', e.target.value)}
                      placeholder="Email" className="col-span-4 text-xs border border-gray-200 rounded-lg px-2 py-2 outline-none focus:border-[#D4AF37]" />
                    <input value={row.phone} onChange={e => updateRow(i, 'phone', e.target.value)}
                      placeholder="Phone" className="col-span-2 text-xs border border-gray-200 rounded-lg px-2 py-2 outline-none focus:border-[#D4AF37]" />
                    <select value={row.gym_tier} onChange={e => updateRow(i, 'gym_tier', e.target.value)}
                      className="col-span-2 text-xs border border-gray-200 rounded-lg px-2 py-2 outline-none focus:border-[#D4AF37]">
                      <option value="basic">Basic</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                    <button onClick={() => removeRow(i)} className="text-gray-300 hover:text-red-500 col-span-1">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={addRow} className="flex items-center gap-1 text-xs text-[#D4AF37] mt-2 hover:underline" data-testid="add-import-row-btn">
                <Plus size={12} /> Add Row
              </button>
            </div>

            <button onClick={handleImport} disabled={importing}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#c9a030] hover:from-[#c9a030] hover:to-[#D4AF37] text-white py-3 rounded-full text-sm font-medium disabled:opacity-60 transition-all"
              data-testid="confirm-import-btn">
              {importing ? 'Importing...' : 'Import Members'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
